import type { UIMessage } from 'ai';
import type { Chat as PersistedChat } from '@shared/types/chat';
import { createScopedLogger } from '~/utils/logger';
import type { ChatHistoryItem } from './useChatHistory';
import type { Snapshot } from './types';
import { apiGetChats, apiGetChat, apiGetChatByUrlId, apiSaveChat, apiDeleteChat } from '~/lib/api/chats';

export interface IChatMetadata {
  gitUrl?: string;
  gitBranch?: string;
  projectType?: 'landing';
  hasStaticPreview?: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
}

export interface Chat {
  id: string;
  description?: string;
  messages: UIMessage[];
  timestamp: string;
  urlId?: string;
  metadata?: IChatMetadata;
}

const logger = createScopedLogger('ChatHistory');

export async function getAll(): Promise<ChatHistoryItem[]> {
  try {
    const chats = await apiGetChats();
    return chats.map((c) => ({
      id: c.id,
      urlId: c.urlId ?? undefined,
      description: c.description ?? undefined,
      messages: [],
      timestamp: c.updatedAt || c.createdAt,
      metadata: c.metadata as IChatMetadata | undefined,
      projectId: c.projectId ?? undefined,
      activeStreamId: c.activeStreamId ?? null,
    }));
  } catch (error) {
    logger.error('Failed to fetch chats', error);
    return [];
  }
}

export async function setMessages(
  id: string,
  messages: UIMessage[],
  urlId?: string,
  description?: string,
  _timestamp?: string,
  metadata?: IChatMetadata,
  projectId?: string,
  snapshot?: Snapshot,
): Promise<PersistedChat> {
  return apiSaveChat({
    id,
    messages,
    urlId,
    description,
    metadata: metadata as any,
    projectId,
    snapshot: snapshot
      ? {
          chatIndex: snapshot.chatIndex,
          files: snapshot.files,
          summary: snapshot.summary,
        }
      : undefined,
  });
}

export async function getMessages(id: string): Promise<ChatHistoryItem | undefined> {
  try {
    const chat = await apiGetChat(id);
    return mapChatToHistoryItem(chat);
  } catch {
    // Try by urlId
    try {
      const chat = await apiGetChatByUrlId(id);
      return mapChatToHistoryItem(chat);
    } catch {
      return undefined;
    }
  }
}

export async function deleteById(id: string): Promise<void> {
  await apiDeleteChat(id);
}

export async function getNextId(): Promise<string> {
  /*
   * Server uses UUIDs, but the existing code expects incrementing string IDs.
   * Generate a short random ID that's compatible with URL paths.
   */
  return crypto.randomUUID().split('-')[0];
}

export async function getUrlId(id: string): Promise<string> {
  /*
   * For new chats, just return the id as the urlId.
   * The server handles uniqueness.
   */
  return id;
}

export async function forkChat(chatId: string, messageId: string): Promise<string> {
  const chat = await getMessages(chatId);

  if (!chat) {
    throw new Error('Chat not found');
  }

  const messageIndex = chat.messages.findIndex((msg) => msg.id === messageId);

  if (messageIndex === -1) {
    throw new Error('Message not found');
  }

  const messages = chat.messages.slice(0, messageIndex + 1);

  return createChatFromMessages(chat.description ? `${chat.description} (fork)` : 'Forked chat', messages);
}

export async function duplicateChat(id: string): Promise<string> {
  const chat = await getMessages(id);

  if (!chat) {
    throw new Error('Chat not found');
  }

  return createChatFromMessages(`${chat.description || 'Chat'} (copy)`, chat.messages);
}

export async function createChatFromMessages(
  description: string,
  messages: UIMessage[],
  metadata?: IChatMetadata,
  projectId?: string,
): Promise<string> {
  const newId = await getNextId();

  await setMessages(newId, messages, newId, description, undefined, metadata, projectId);

  return newId;
}

export async function updateChatDescription(id: string, description: string): Promise<void> {
  const chat = await getMessages(id);

  if (!chat) {
    throw new Error('Chat not found');
  }

  if (!description.trim()) {
    throw new Error('Description cannot be empty');
  }

  await setMessages(id, chat.messages, chat.urlId, description, chat.timestamp, chat.metadata);
}

export async function updateChatMetadata(id: string, metadata: IChatMetadata | undefined): Promise<void> {
  const chat = await getMessages(id);

  if (!chat) {
    throw new Error('Chat not found');
  }

  await setMessages(id, chat.messages, chat.urlId, chat.description, chat.timestamp, metadata);
}

export async function getSnapshot(chatId: string): Promise<Snapshot | undefined> {
  try {
    const chat = await apiGetChat(chatId);
    return chat.snapshot ?? undefined;
  } catch {
    return undefined;
  }
}

function mapChatToHistoryItem(chat: any): ChatHistoryItem {
  return {
    id: chat.id,
    urlId: chat.urlId ?? undefined,
    description: chat.description ?? undefined,
    messages: chat.messages || [],
    timestamp: chat.updatedAt || chat.createdAt,
    metadata: chat.metadata as IChatMetadata | undefined,
    projectId: chat.projectId ?? undefined,
    activeStreamId: chat.activeStreamId ?? null,
  };
}
